import React, { useState, useEffect, useContext } from "react";
import { Box, Card, CardContent, Typography, Paper, Grid, ListItem, List, Button, Tab, Snackbar, Alert } from "@mui/material";
import { getSharedQueries, saveQuery } from "../services";
import { UserContext } from "../contexts";
import RWEdatalab_world_map from "../assets/images/RWEdatalab_world_map.png";
import QueryInfoImage from "../assets/images/query-info.png";
import { DataCertifications } from "../components/DataCertifications";

const Dashboard = () => {
  const [value, setValue] = useState('Diagnosis');
  const [loading, setLoading] = useState(false);
  const { userAppSettings, setUserAppSettings } = useContext(UserContext);
  const [sharedQueries, setSharedQueries] = useState([]);
  const [snackbarInfo, setSnackbarInfo] = useState({
    open: false,
    message: "",
    severity: "",
  });

  useEffect(() => {
    setLoading(true);
    getSharedQueries(userAppSettings.id).then((data) => {
      if (data.success) {
        setSharedQueries(data.data);
      } else {
        setSharedQueries([]);
      }
      setLoading(false);
    })
      .catch(err => {
        setLoading(false);
        setSnackbarInfo({
          open: true,
          message: err,
          severity: "error",
        })
      });
  }, []);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const copyQuery = (query, additionalNamePart) => {
    const myInit = {
      body: {
        queryID: userAppSettings.id + new Date().toISOString(),
        userID: userAppSettings.id,
        queryName: `${query.QueryName}${additionalNamePart}`,
        queryDescription: query.QueryDescription ?? '',
        queryDefinition: query.QueryDefinition,
        tags: query.Tags ?? [],
        isShareable: false
      }
    };

    saveQuery(myInit).then(() => {
      setSnackbarInfo({
        open: true,
        message: "Query Copied Successfully!",
        severity: "success",
      });
    });
  }

  const renderSharedActionButtons = (row) => {
    return (
      <strong>
        <Button
          color="primary"
          size="small"
          style={{ marginLeft: 8 }}
          onClick={() => {
            copyQuery(row, "");
          }}
        >
          Copy Query
        </Button>
      </strong>
    )
  }

  return (
    <Box component="main" sx={{ padding: '16px', height: '100%' }}>
      <Grid container spacing={2}>
        <Grid size={6} sx={{ padding: '8px' }}>
          <Grid>
            <Grid sx={{
              fontSize: '1.5rem'
            }}>Welcome to <strong>RWE</strong>data<strong>Lab</strong></Grid>
            <Grid container sx={{
              marginTop: '8px',
            }}>
              <Grid size={12}>
                Available to all IUSM students, residents, and faculty, the RWE DataLab was designed with the goal of accelerating the discovery of meaningful insights into practice-changing research and medicine.  IUSM researchers can utilize the RWE DataLab to build patient cohorts and extract desired data elements from licensed RWE datasets in support of scholarly articles, poster presentations, and grant submissions.
              </Grid>
              <Grid size={12} textAlign={"center"}>
                <img src={RWEdatalab_world_map} alt="Age Group Chart" style={{
                  width: '70%'
                }} />
              </Grid>
            </Grid>
          </Grid>
          <Grid sx={{
            marginTop: '16px',
          }}>
            <Grid sx={{
              fontSize: '1.5rem'
            }}>Getting Started: </Grid>
            <Grid size={12}>
              <Typography variant="p">The RWE DataLab uses a state-of-the-art database abstraction platform to describe various data sources. Users are able to query their desired data source using the same application interface reducing the learning curve needed to query each data source in its native query language (e.g. SQL, JSON, XML)</Typography>
            </Grid>
            <Grid size={12} sx={{
              marginTop: '16px',
            }}>
              <Typography variant="p">{`Developed to assist researchers of all technical abilities, the RWE DataLab’s intuitive design makes it easy to build a desired patient cohort, choose the required data elements, and then immediately download your data for analysis.`}</Typography>
            </Grid>
          </Grid>
          <Grid sx={{
            marginTop: '16px',
          }}>
            <Grid sx={{
              fontSize: '1.5rem'
            }}>First Step: Query Builder</Grid>
          </Grid>
          <Grid size={12}>
            <Typography variant="p">
              On the left side of the screen displays the pages of the portal, Dashboard (homepage), Query Builder, and Saved Queries.
            </Typography>
          </Grid>
          <Grid size={12} sx={{
            marginTop: '16px',
          }}>
            <Typography variant="p">
              Selecting the Query Builder icon will direct you to page shown below:
            </Typography>
            <img src={QueryInfoImage} alt="Query Info" style={{ width: '100%' }} />
          </Grid>
          <Grid size={12}>
            <Typography variant="p">
              You will then create a title for your query as well as any related tags and descriptions. (The tags and descriptions are not mandatory)
            </Typography>
          </Grid>
          <Grid size={12} sx={{
            marginTop: '16px',
          }}>
            <Typography variant="p">
              At the bottom of the page is where you can choose your Database. Currently, you have access to the IUSM Cardio and the IUSM Psych databases.
            </Typography>
          </Grid>
          <Grid size={12} sx={{
            marginTop: '16px',
          }}>
            <Typography variant="p">
              After selecting a database you wish to explore, you can proceed to the Query Builder page by selecting “Begin”
            </Typography>
          </Grid>
          <Grid sx={{
            marginTop: '16px',
          }}>
            <Grid sx={{
              fontSize: '1.5rem'
            }}>From Query to Data Extraction</Grid>
          </Grid>
          <Grid size={12} sx={{
            marginTop: '16px',
          }}>
            <Typography variant="p">
              RWE DataLab allows researchers to easily query by inclusion or exclusion criteria via filters and sub-filters, then refresh patient counts any step of the way.
            </Typography>
          </Grid>
          <Grid size={12} sx={{
            marginTop: '16px',
          }}>
            <Typography variant="p">
              All queries are auto-saved for researchers, and patient counts reflect a data period of up to 20 years unless otherwise specified. Once you have finished adding your criteria, you can execute the report for download. In this step, you can add additional fields from the database. Your criteria fields are automatically added.
            </Typography>
          </Grid>
        </Grid>
        <Grid size={6}>
          <Grid size={12} sx={{
            border: '1px solid',
            borderColor: 'accentPanel.border',
            backgroundColor: 'accentPanel.bg',
            color: 'text.primary',
            borderRadius: 2,
            padding: '8px'
          }}>
            <Grid sx={{ textAlign: 'center', fontSize: '1.5rem' }}>About DataLab’s Real-World Data</Grid>
            <Grid sx={{
              marginTop: '8px',
            }}>All data accessed through the <strong>RWE DataLab</strong> is sourced from nationally deployed HIPAA compliant electronic health record systems through a collective network of 37,000 community-based primary care and specialty care providers across all 50 States. The data has been de-identified and certified by Independent Expert Determination with 99.7% efficacy.</Grid>
            <Grid sx={{
              marginTop: '16px',
            }}>IUSM Researchers currently have access to two national datasets through the RWE DataLab portal: </Grid>
            <Grid sx={{
              marginTop: '16px',
            }}>
              <strong>- RWE Cardiology Structured Data –</strong> Considered one of the richest real-world datasets of its kind in the US, the IUSM Cardio Dataset contains up to 20 years of longitudinal de-identified structured and unstructured data for nearly 3.5 Million patients across more than 45 million encounters.</Grid>
            <Grid sx={{
              marginTop: '16px',
            }}><strong>- RWE Psychiatry Data –</strong> The IUSM Psych Dataset is one of the largest in the US encompassing more than 4.8 Million patients with up to 20 years of longitudinal structured and unstructured data.</Grid>
            <Grid container sx={{
              backgroundColor: 'accentPanel.contrastBg',
              color: 'accentPanel.contrastText',
              padding: '8px',
              borderRadius: '16px',
              marginTop: '16px',
              marginBottom: '16px',
            }}>
              <Grid size={12} sx={{
                textAlign: 'center'
              }}>Enriched RWE Data Elements</Grid>
              <Grid size={6}>
                <List>
                  <ListItem>- Patient Demographics</ListItem>
                  <ListItem>- Vital Signs</ListItem>
                  <ListItem>- Lab Results (LOINC Codes)</ListItem>
                  <ListItem>- Medical History</ListItem>
                  <ListItem>- Medications: RxNorm</ListItem>
                  <ListItem>- CPT Codes for procedures</ListItem>
                  <ListItem>- ICD10 Codes for Diagnosis</ListItem>
                  <ListItem>- Allergies</ListItem>
                  <ListItem>- Stroke Risk Score</ListItem>
                  <ListItem>- Medical Order History</ListItem>
                </List>
              </Grid>
              <Grid size={6}>
                <List>
                  <ListItem>- SNOMED Codified Data</ListItem>
                  <ListItem>- Family History</ListItem>
                  <ListItem>- Place of Service Codes</ListItem>
                  <ListItem>- Diagnosis Status</ListItem>
                  <ListItem>- Chief Complaint</ListItem>
                  <ListItem>- Cardiovascular Risk Score</ListItem>
                  <ListItem>- Review of Systems</ListItem>
                  <ListItem>- Procedure History</ListItem>
                  <ListItem>- Patient Type: Medicare, Medicaid and Commercial Payer</ListItem>
                </List>
              </Grid>
            </Grid>
            <Grid sx={{ textAlign: 'center', fontSize: '1.2rem' }}>RWE Data Quality, Certifications, & Regulatory Standards</Grid>
            {/* <Grid xs={12}>
              <img src={CertificationsImage} alt="Certifications" style={{ width: '100%' }} />
            </Grid> */}
            <DataCertifications />
          </Grid>
        </Grid>

      </Grid>
    </Box >
  );
}

export default Dashboard;
